import React, { useCallback, useEffect, useState } from "react";
import HelpBubble from "./HelpBubble";
import { PRICING_OVERVIEW_HELP } from "./helpTexts";
import { getPricingSettings, savePricingSettings } from "../../../api/marketplacePricingApi";
import BreakevenSettingsForm from "./BreakevenSettingsForm";
import PriceExplainPanel from "./PriceExplainPanel";
import PriceRulesPanel from "./PriceRulesPanel";
import { PLATFORM_LABELS, formatApiError } from "./pricingFormat";
import "./marketplace-pricing.css";

const SUBSECTIONS = [
    ["profile", "Безубыточность"],
    ["rules", "Скидки и правила"],
    ["explain", "Разбор цены"],
];

// Вкладка «Цены» аккаунта площадки: профиль безубыточности, правила бренд/SKU, разбор цены.
// active=false — данные не грузим (страница Prom держит секции в DOM через display:none).
export default function MarketplacePricingTab({ platform, accountId, active = true, onModeChange }) {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [subsection, setSubsection] = useState("profile");

    const load = useCallback(async () => {
        if (!accountId || !active) return;
        setLoading(true);
        setError("");
        try {
            setSettings(await getPricingSettings(platform, accountId));
        } catch (apiError) {
            setError(formatApiError(apiError, "Не удалось загрузить настройки цен."));
        } finally {
            setLoading(false);
        }
    }, [platform, accountId, active]);

    useEffect(() => {
        load();
    }, [load]);

    const save = async (payload) => {
        setSaving(true);
        try {
            const saved = await savePricingSettings(platform, accountId, payload);
            setSettings(saved);
            if (onModeChange) onModeChange(saved.pricing_mode);
        } finally {
            setSaving(false);
        }
    };

    if (!accountId) {
        return (
            <div className="mp-pricing">
                <div className="mp-callout">Сначала выберите или создайте аккаунт.</div>
            </div>
        );
    }

    const platformLabel = PLATFORM_LABELS[platform] || platform;
    const mode = settings?.pricing_mode || "legacy";

    return (
        <div className="mp-pricing">
            <div className="mp-card">
                <HelpBubble title="Как считается цена на площадке">{PRICING_OVERVIEW_HELP}</HelpBubble>
                <div className="mp-card-head">
                    <div>
                        <h2 className="mp-title">Цены {platformLabel}</h2>
                        <p className="mp-lead">
                            Цена = (закупка + фикс площадки + надбавка диапазона + прибыль) ÷ (1 − проценты площадки). Дальше
                            правила бренда или товара, доп. наценка отправителя и округление.
                        </p>
                    </div>
                    {settings ? (
                        mode === "breakeven" ? (
                            <span className="mp-pill mp-pill-good">Режим: от безубыточности</span>
                        ) : (
                            <span className="mp-pill mp-pill-warn">Режим: старая цена</span>
                        )
                    ) : null}
                </div>
                <div className="mp-subnav" role="tablist" aria-label="Разделы цен">
                    {SUBSECTIONS.map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={subsection === key}
                            className={subsection === key ? "active" : ""}
                            onClick={() => setSubsection(key)}
                        >
                            {label}
                            {key === "rules" && settings?.active_rules_count ? ` (${settings.active_rules_count})` : ""}
                        </button>
                    ))}
                </div>
                {mode === "legacy" && settings ? (
                    <div className="mp-callout">
                        Сейчас цены считаются по-старому. Профиль и правила можно заполнить заранее, они начнут
                        действовать после переключения режима.
                    </div>
                ) : null}
            </div>

            {loading && !settings ? <div className="mp-callout" role="status">Загрузка…</div> : null}
            {error ? <div className="mp-callout mp-callout-error" role="alert">{error}</div> : null}

            {settings && subsection === "profile" ? (
                <BreakevenSettingsForm settings={settings} platformLabel={platformLabel} saving={saving} onSave={save} />
            ) : null}
            {settings && subsection === "rules" ? <PriceRulesPanel platform={platform} accountId={accountId} /> : null}
            {settings && subsection === "explain" ? (
                <PriceExplainPanel platform={platform} accountId={accountId} settings={settings} />
            ) : null}
        </div>
    );
}
